//  @flow
//  ----------------------------------------------------------------------
//  <copyright file="Izenda.ItemsToCopy.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import PropTypes from 'prop-types';
import Input from 'share/components/controls/Izenda.Input';
import Label from 'share/components/controls/Izenda.Label';
import connect from 'share/enhancers/Izenda.Connect';

@connect()
export default class ItemsToCopy extends Component {
  static propTypes: {
    store: PropTypes.object.isRequired,
    item: PropTypes.object.isRequired
  };

  render() {
    const { item, localization } = this.props;

    const CHECKBOX_DEFINITIONS = {
      copyDataModel: [
        this.onCopyDataModelChanged,
        localization.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_DATA_MODEL'),
        item.disableItemsToCopy
      ],
      copyAdvancedSettings: [
        this.onCopyAdvancedSettingsChanged,
        localization.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_ADVANCED_DATA_SETTING'),
        item.disableItemsToCopy
      ],
      copyDashboard: [
        this.onCopyDashboardChanged,
        localization.getText('COMMON_LABEL_DASHBOARDS'),
        item.disableItemsToCopy
      ],
      copyReport: [this.onCopyReportChanged, localization.getText('COMMON_LABEL_REPORTS'), item.disableItemsToCopy],
      copyTenantPermissions: [
        this.onCopyTenantPermissionsChanged,
        localization.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_TENANT_PERMISSIONS'),
        item.disableItemsToCopy || item.isSystemSource
      ],
      copyRoles: [
        this.onCopyRolesChanged,
        localization.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_ROLES'),
        item.disableItemsToCopy || item.isSystemSource
      ],
      copyRolePermission: [
        this.onCopyRolePermissionChanged,
        localization.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_ROLE_PERMISSIONS'),
        item.disableItemsToCopy || item.isSystemSource
      ]
    };

    return (
      <GridLayout>
        <GridLayout.Row>
          <GridLayout.Col cols={12}>
            <span className='text-bold u_inlineBlock'>
              {localization.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_ITEMS_TO_COPY')}
            </span>
            <span className='u_inlineBlock u_alignTop'>
              {Object.keys(CHECKBOX_DEFINITIONS).map(key => {
                return this.renderCheckbox(CHECKBOX_DEFINITIONS, key);
              })}
            </span>
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );
  }

  renderCheckbox = (checkboxs: any, key: string) => {
    const { item } = this.props;
    const checkboxDef = checkboxs[key];
    const anchorSetting = checkboxDef[3];
    const scrollIntoViewFunc = this.scrollIntoView.bind(null, anchorSetting);
    const classNames = 'u_paddingRight u_alignMiddle u_inlineBlock u_textLink u_textUnderline u_marginBottomSmall';

    return (
      <span key={key}>
        <Input type='checkbox' checked={item[key]} id={key} onChange={checkboxDef[0]} disabled={checkboxDef[2]} />

        {item[key] && anchorSetting ? (
          <span className={classNames} onClick={scrollIntoViewFunc}>
            {checkboxDef[1]}
          </span>
        ) : (
          <Label className='u_paddingRight u_alignMiddle u_textNormal' htmlFor={key}>
            {checkboxDef[1]}
          </Label>
        )}
      </span>
    );
  };

  /**
   * scroll Into View
   * @param  {object} anchorSetting
   */
  scrollIntoView = (anchorSetting: any) => {
    const element = document.querySelector('#' + anchorSetting);
    element && element.scrollIntoView();
  };

  /**
   * on Copy Data Model Changed
   * @param  {object} event
   */
  onCopyDataModelChanged = (event: any) => {
    const { item } = this.props;
    item.resetWorkspaceTenantsStatus();
    item.setPropertyValue('copyDataModel', event.target.checked);
    item.updateQuerySourceTree();
    item.reCalculateSelectedSources();
  };

  /**
   * on Copy Roles Changed
   * @param  {object} event
   */
  onCopyRolesChanged = (event: any) => {
    const { item } = this.props;
    item.setPropertyValue('copyRoles', event.target.checked);
  };

  /**
   * on Copy Role Permission Changed
   * @param  {object} event
   */
  onCopyRolePermissionChanged = (event: any) => {
    const { item } = this.props;
    item.setPropertyValue('copyRolePermission', event.target.checked);
  };

  /**
   * on Copy Tenant Permissions Changed
   * @param  {object} event
   */
  onCopyTenantPermissionsChanged = (event: any) => {
    const { item } = this.props;
    item.setPropertyValue('copyTenantPermissions', event.target.checked);
  };

  /**
   * on Copy Advanced Settings Changed
   * @param  {object} event
   */
  onCopyAdvancedSettingsChanged = (event: any) => {
    this.props.item.setPropertyValue('copyAdvancedSettings', event.target.checked);
  };

  /**
   * on copy dashboard changed
   * @param  {object} event
   */
  onCopyDashboardChanged = (event: any) => {
    const {
      props,
      props: { item, store }
    } = this;
    const newValue = event.target.checked;

    if (newValue) {
      const loader = props.loader;
      loader.startLoading();
      store.loadDashboards(item.sourceId, true).finally(loader.endLoading.bind(loader));
    }
    item.resetWorkspaceTenantsStatus();
    item.setPropertyValue('copyDashboard', newValue);
  };

  /**
   * on copy report changed
   * @param  {object} event
   */
  onCopyReportChanged = (event: any) => {
    const {
      props,
      props: { item, store }
    } = this;
    const newValue = event.target.checked;

    if (newValue) {
      const loader = props.loader;
      loader.startLoading();
      store.loadReports(item.sourceId, true).finally(loader.endLoading.bind(loader));
    }
    item.resetWorkspaceTenantsStatus();
    item.setPropertyValue('copyReport', newValue);
  };
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/CopyManagement/WorkspaceDetail/AdditionalItems/Izenda.ItemsToCopy.js